import { type ColumnDef } from '@tanstack/react-table'

import { type FunctionComponent, useCallback, useMemo } from 'react'

import { type Account } from '../../types/account'
import { type Attachment } from '../../types/attachment'
import { type CreditNote } from '../../types/creditnote'

import { showAlert } from 'src/actions/alertsActions'
import { closeDrawers, openCreditNoteDrawer } from 'src/actions/drawerActions'
import { startLoading, stopLoading } from 'src/actions/loadingActions'

/* api */
import api from 'src/constants/api'

import moment from 'moment'
import { Iconly } from 'react-iconly'
import { type RootStateOrAny, useDispatch, useSelector } from 'react-redux'

import Table from '../Common/Table/Table'

interface Props {
  creditnotes: CreditNote[]
}

const CreditNoteTable: FunctionComponent<Props> = ({ creditnotes }: Props): JSX.Element => {
  const account: Account = useSelector((state: RootStateOrAny) => state.account)
  const dispatch = useDispatch()

  /* download pdf */
  const downloadPDF = useCallback(
    (event: React.MouseEvent, creditnote_id: string, creditnote_number: string) => {
      event.stopPropagation()
      if (creditnote_number) {
        dispatch(startLoading())

        api
          .get(`/servicenow/account/${account.id}/u_credit_note/${creditnote_id}/attachments`)
          .then((response) => {
            let creditnote_exists = false
            /* check if the pdf exists as an attachment */
            response.data.forEach(async (attachment: Attachment) => {
              if (attachment.name.toLowerCase() === (creditnote_number + '.pdf').toLowerCase()) {
                creditnote_exists = true
                /* download the attachment  */
                return await api
                  .get(`/servicenow/account/${account.id}/u_credit_note/${creditnote_id}/attachment/${attachment.id}`, {
                    responseType: 'blob'
                  })
                  .then((response) => {
                    const blob = new Blob([response.data])
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')

                    a.style.display = 'none'
                    a.href = url
                    a.download = attachment.name

                    document.body.appendChild(a)
                    a.click()

                    dispatch(stopLoading())

                    return window.URL.revokeObjectURL(url)
                  })
                  .catch((error) => {
                    /* alert the user that the download failed */
                    dispatch(
                      showAlert({
                        type: 'error',
                        message: 'We were unable to download the PDF for credit note ' + creditnote_number + '.',
                        component: 'CreditNoteTable',
                        error
                      })
                    )

                    dispatch(stopLoading())
                  })
              }
            })

            /* alert the user that the pdf couldn't be found */
            if (!creditnote_exists) {
              dispatch(
                showAlert({
                  type: 'info',
                  message: "We couldn't find the PDF for credit note " + creditnote_number + '. It may not be ready yet.',
                  component: 'CreditNoteTable'
                })
              )
            }

            dispatch(stopLoading())
          })
          .catch((error) => {
            dispatch(
              showAlert({
                type: 'error',
                message: 'We were unable to retrieve the PDF for credit note ' + creditnote_number + '.',
                component: 'CreditNoteTable',
                error
              })
            )

            dispatch(stopLoading())
          })
      }
    },
    [account.id, dispatch]
  )

  const columns = useMemo(
    () =>
      [
        {
          accessorKey: 'number',
          header: 'Number'
        },
        {
          accessorKey: 'tax_date',
          header: 'Tax Date',
          cell: (info: any) => moment(info.getValue()).format('DD/MM/YY'),
          sortingFn: (a: any, b: any, id: string) => moment(a.getValue(id)).unix() - moment(b.getValue(id)).unix()
        },
        {
          accessorKey: 'subtotal',
          header: 'Subtotal (£)'
        },
        {
          accessorKey: 'tax',
          header: 'Tax (£)'
        },
        {
          accessorKey: 'total',
          header: 'Total (£)'
        },
        {
          accessorKey: 'pdf',
          header: 'PDF',
          cell: (info) => (
            <Iconly
              set="light"
              name="PaperDownload"
              className="cursor-pointer"
              onClick={(event: React.MouseEvent) => downloadPDF(event, info.row.original.id, info.row.original.number)}
            />
          )
        }
      ] satisfies ColumnDef<(typeof creditnotes)[number]>[],
    [downloadPDF]
  )

  return (
    <Table
      data={creditnotes}
      columns={columns}
      onRowClick={(row) => {
        dispatch(closeDrawers())
        dispatch(openCreditNoteDrawer(row))
      }}
    />
  )
}

export default CreditNoteTable
